import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';

/**
 * Hook for checking user permissions
 */
export const usePermissions = () => {
  const reduxUser = useSelector((state: any) => state?.Login?.user);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Always merge data from both sources to ensure menuPermissions are included
    try {
      const authUser = sessionStorage.getItem('authUser');
      let sessionUser = null;
      
      if (authUser) {
        const parsed = JSON.parse(authUser);
        sessionUser = parsed?.user;
      }
      
      // Merge Redux user with session user to ensure menuPermissions are included
      if (reduxUser && Object.keys(reduxUser).length > 0) {
        const mergedUser = {
          ...reduxUser,
          menuPermissions: sessionUser?.menuPermissions || reduxUser.menuPermissions || []
        };
        setUser(mergedUser);
      } else if (sessionUser) {
        setUser(sessionUser);
      }
    } catch (error) {
      console.error('Error in PermissionHooks:', error);
    }
  }, [reduxUser]);

  /**
   * Check if user has a specific permission
   * DEPRECATED: Feature permissions removed - using only menuPermissions
   * Keeping for backward compatibility but always returns false
   */
  const hasPermission = (feature: string): boolean => {
    // Special handling for document capture permissions (keeping for now)
    if (feature === 'document_capture_review') {
      return hasRole('admin') || hasRole('accounting');
    }
    
    // Feature permissions removed - always return false
    return false;
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (features: string[]): boolean => {
    if (!user || !user.permissions) return false;
    return features.some(feature => user.permissions.includes(feature));
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (features: string[]): boolean => {
    if (!user || !user.permissions) return false;
    return features.every(feature => user.permissions.includes(feature));
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (roleName: string): boolean => {
    if (!user) return false;
    // Check both level_name and role fields for compatibility
    return user.level_name === roleName || user.role === roleName;
  };

  /**
   * Check if user is admin
   */
  const isAdmin = (): boolean => {
    return user?.level_id === 1 || hasRole('admin');
  };

  /**
   * Check if user is manager
   */
  const isManager = (): boolean => {
    return user?.level_id === 256 || hasRole('manager');
  };

  /**
   * Check if user is poweruser
   */
  const isPowerUser = (): boolean => {
    return user?.level_id === 128 || hasRole('poweruser');
  };

  /**
   * Check if user is frontoffice
   */
  const isFrontOffice = (): boolean => {
    return user?.level_id === 32 || hasRole('frontoffice');
  };

  /**
   * Check if user is backoffice
   */
  const isBackOffice = (): boolean => {
    return user?.level_id === 64 || hasRole('backoffice');
  };

  /**
   * Get user's role name
   */
  const getRoleName = (): string => {
    return user?.level_name || 'user';
  };

  /**
   * Get user's level ID
   */
  const getLevelId = (): number => {
    return user?.level_id || 0;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isAdmin,
    isManager,
    isPowerUser,
    isFrontOffice,
    isBackOffice,
    getRoleName,
    getLevelId,
    permissions: user?.permissions || [],
    user
  };
};

/**
 * Feature permissions removed - using only menuPermissions now
 * FEATURES constant kept for backward compatibility with minimal definitions
 */
export const FEATURES = {
  KASSABUCH: 'KASSABUCH',
  EINGANGSRECHNUNGEN_BH: 'EINGANGSRECHNUNGEN_BH',
  AUSGANGSRECHNUNGEN: 'AUSGANGSRECHNUNGEN',
  MITARBEITERVERWALTUNG: 'MITARBEITERVERWALTUNG'
} as const;

/**
 * Role constants for type safety
 */
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  POWERUSER: 'poweruser',
  USER: 'user',
  FRONTOFFICE: 'frontoffice',
  BACKOFFICE: 'backoffice'
} as const;